import { useMemo } from 'react';

import {
    isLaunchCommunicationBlock,
    isLegacyScheduleBlock,
    type CommunicationBlockDefinition,
    type CommunicationBlockIdValue,
} from './communicationBlocks';
import {
    cardCurrentMessage,
    cardCurrentTitle,
    cardKey,
    type CommunicationCardKey,
    type CommunicationCardState,
} from './communicationCardState';
import { type CommunicationBlockViewModel } from './components/Communications/Communications';

import type { useAmelioTranslations } from '@/hooks';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { Dispatch, SetStateAction } from 'react';
import type { Descendant } from 'slate';

import { findGroupEvaluationByType } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { CommunicationEmailAudience } from '@/pages/CycleEvaluation/Communications/communicationEmailAudience';

interface UseCommunicationBlockViewModelsParams {
    visibleBlocks: CommunicationBlockDefinition[];
    group: CycleEvaluationGroup | undefined;
    cards: Partial<Record<CommunicationCardKey, CommunicationCardState>>;
    blockEnabled: Partial<Record<CommunicationBlockIdValue, boolean>>;
    setBlockEnabled: Dispatch<SetStateAction<Partial<Record<CommunicationBlockIdValue, boolean>>>>;
    cycleEvaluationGroupId: string | undefined;
    defaultLanguage: string;
    t: ReturnType<typeof useAmelioTranslations>['t'];
    updateCard: (key: CommunicationCardKey, patch: Partial<CommunicationCardState>) => void;
    updateCardText: (
        key: CommunicationCardKey,
        language: string,
        patch: { title?: string; message?: Descendant[] },
    ) => void;
    onCardLanguageChange: (
        key: CommunicationCardKey,
        previousLanguage: string,
        nextLanguage: string,
    ) => void;
    buildOnTestEmail: (
        key: CommunicationCardKey,
        cycleEvaluationId?: string,
    ) => () => Promise<void>;
    buildOnGroupTestEmail: (key: CommunicationCardKey) => () => Promise<void>;
    isSendingTestEmail: boolean;
    isSendingGroupTestEmail: boolean;
    isReadOnly: boolean;
    /** Whether one block can only be read, while the rest of the step stays editable. */
    isBlockReadOnly?: (id: string) => boolean;
}

/**
 * Builds the `Communications` component's own `blocks` prop — one view model per visible block,
 * with its manager and (when the block carries one) employee card fully wired to this step's
 * state and persistence callbacks.
 */
export const useCommunicationBlockViewModels = ({
    visibleBlocks,
    group,
    cards,
    blockEnabled,
    setBlockEnabled,
    cycleEvaluationGroupId,
    defaultLanguage,
    t,
    updateCard,
    updateCardText,
    onCardLanguageChange,
    buildOnTestEmail,
    buildOnGroupTestEmail,
    isSendingTestEmail,
    isSendingGroupTestEmail,
    isReadOnly,
    isBlockReadOnly = () => false,
}: UseCommunicationBlockViewModelsParams) => {
    const cycleEvaluationIdByBlock = useMemo(() => {
        const map: Partial<Record<CommunicationBlockIdValue, string>> = {};

        for (const block of visibleBlocks) {
            if (isLaunchCommunicationBlock(block)) {
                map[block.id] = findGroupEvaluationByType(group, block.evaluationType)?.id;
            }
        }

        return map;
    }, [visibleBlocks, group]);

    const blocks: CommunicationBlockViewModel[] = useMemo(
        () =>
            visibleBlocks.map((block) => {
                const managerKey = cardKey(block.id, CommunicationEmailAudience.MANAGER);
                const employeeKey = cardKey(block.id, CommunicationEmailAudience.EMPLOYEE);
                const managerCard = cards[managerKey];
                const employeeCard = block.hasEmployeeCard ? cards[employeeKey] : undefined;
                const cycleEvaluationId = cycleEvaluationIdByBlock[block.id];
                const isBlockTestEmailReachable = isLaunchCommunicationBlock(block)
                    ? !!cycleEvaluationId
                    : !!cycleEvaluationGroupId;
                const testEmailUnavailableReason = isBlockTestEmailReachable
                    ? undefined
                    : t('cycleEvaluationCommunications.email.testSendUnavailable');
                const isReadOnlyBlock = isReadOnly || isBlockReadOnly(block.id);
                const onBlockTestEmail = isLaunchCommunicationBlock(block)
                    ? (key: CommunicationCardKey) => buildOnTestEmail(key, cycleEvaluationId)
                    : (key: CommunicationCardKey) => buildOnGroupTestEmail(key);

                return {
                    id: block.id,
                    title: t(block.labelKey),
                    blockEnabled: blockEnabled[block.id] ?? true,
                    onBlockEnabledChange: (next: boolean) => {
                        setBlockEnabled((current) => ({ ...current, [block.id]: next }));

                        // Only the legacy blocks need the command relayed: the others persist
                        // their own switch, which the send path reads on its own.
                        if (isLegacyScheduleBlock(block)) {
                            updateCard(managerKey, { enabled: next });
                            updateCard(employeeKey, { enabled: next });
                        }
                    },
                    isReadOnly: isReadOnlyBlock,
                    managerCardProps: {
                        enabled: managerCard?.enabled ?? true,
                        onEnabledChange: (next: boolean) =>
                            updateCard(managerKey, { enabled: next }),
                        emailLanguage: managerCard?.language ?? defaultLanguage,
                        emailTitle: cardCurrentTitle(managerCard),
                        emailMessage: cardCurrentMessage(managerCard),
                        editorKey: `${managerKey}-${managerCard?.language ?? defaultLanguage}`,
                        testEmailAddress: managerCard?.testEmailAddress ?? '',
                        onEmailLanguageChange: (value: string) =>
                            onCardLanguageChange(
                                managerKey,
                                managerCard?.language ?? defaultLanguage,
                                value,
                            ),
                        onEmailTitleChange: (value: string) =>
                            updateCardText(managerKey, managerCard?.language ?? defaultLanguage, {
                                title: value,
                            }),
                        onEmailMessageChange: (value: Descendant[]) =>
                            updateCardText(managerKey, managerCard?.language ?? defaultLanguage, {
                                message: value,
                            }),
                        onTestEmailAddressChange: (value: string) =>
                            updateCard(managerKey, { testEmailAddress: value }),
                        onTestEmail: onBlockTestEmail(managerKey),
                        isSendingTestEmail: isSendingTestEmail || isSendingGroupTestEmail,
                        isReadOnly: isReadOnlyBlock,
                        unavailableReason: testEmailUnavailableReason,
                    },
                    employeeCardProps: employeeCard
                        ? {
                              enabled: employeeCard.enabled,
                              onEnabledChange: (next: boolean) =>
                                  updateCard(employeeKey, { enabled: next }),
                              emailLanguage: employeeCard.language,
                              emailTitle: cardCurrentTitle(employeeCard),
                              emailMessage: cardCurrentMessage(employeeCard),
                              editorKey: `${employeeKey}-${employeeCard.language}`,
                              testEmailAddress: employeeCard.testEmailAddress,
                              onEmailLanguageChange: (value: string) =>
                                  onCardLanguageChange(employeeKey, employeeCard.language, value),
                              onEmailTitleChange: (value: string) =>
                                  updateCardText(employeeKey, employeeCard.language, {
                                      title: value,
                                  }),
                              onEmailMessageChange: (value: Descendant[]) =>
                                  updateCardText(employeeKey, employeeCard.language, {
                                      message: value,
                                  }),
                              onTestEmailAddressChange: (value: string) =>
                                  updateCard(employeeKey, { testEmailAddress: value }),
                              onTestEmail: onBlockTestEmail(employeeKey),
                              isSendingTestEmail: isSendingTestEmail || isSendingGroupTestEmail,
                              isReadOnly: isReadOnlyBlock,
                              unavailableReason: testEmailUnavailableReason,
                          }
                        : undefined,
                };
            }),
        [
            visibleBlocks,
            cards,
            blockEnabled,
            cycleEvaluationIdByBlock,
            cycleEvaluationGroupId,
            defaultLanguage,
            t,
            updateCard,
            updateCardText,
            onCardLanguageChange,
            buildOnTestEmail,
            buildOnGroupTestEmail,
            isSendingTestEmail,
            isSendingGroupTestEmail,
            isReadOnly,
            isBlockReadOnly,
        ],
    );

    return { blocks };
};
